import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudSync as CloudSyncIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { format } from "date-fns";
import api from "../utils/api";
import {
  applyPersistedColumnWidths,
  mergeColumnWidth,
} from "../utils/dataGridColumnWidths";

const AssetRegister = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [assets, setAssets] = useState([]);
  const [azureConfigs, setAzureConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [entraidUsers, setEntraidUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formError, setFormError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    deviceType: "",
    status: "",
    assetType: "", // "hardware", "azure", "sharepoint", or "" for all
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 100,
    total: 0,
  });
  const [columnWidths, setColumnWidths] = useState({});
  const columnWidthSaveTimeoutRef = useRef(null);

  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [formData, setFormData] = useState({
    assetTag: "",
    employeeId: "",
    employeeEmail: "",
    employeeName: "",
    employeeIdSecondary: "",
    employeeEmailSecondary: "",
    employeeNameSecondary: "",
    deviceType: "Laptop",
    osType: "Windows",
    serialNumber: "",
    allocatedDate: new Date(),
    cost: "",
    includesVat: true,
    vatRate: 20.0,
    notes: "",
    status: "active",
    classification: "Internal",
    lifecycleStage: null,
    // Azure-specific fields
    assetId: "",
    assetName: "",
    assetType: "",
    assetTypeFriendly: "",
    location: "",
    azureResourceGroup: "",
    azureSubscriptionId: "",
    azureCreatedAt: null,
    // SharePoint-specific fields
    sharepointSiteCollection: "",
    sharepointCreatedAt: null,
    sharepointSizeBytes: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [syncingAzure, setSyncingAzure] = useState(false);
  const [syncingSharePoint, setSyncingSharePoint] = useState(false);
  const [allocationHistory, setAllocationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [reallocateDialogOpen, setReallocateDialogOpen] = useState(false);
  const [disposeDialogOpen, setDisposeDialogOpen] = useState(false);
  const [reallocateData, setReallocateData] = useState({
    employeeId: "",
    employeeEmail: "",
    employeeName: "",
    reason: "",
    notes: "",
  });
  const [disposeData, setDisposeData] = useState({
    disposalReason: "",
    disposalNotes: "",
    dataWipeMethod: "",
    disposalCertificate: "",
    disposalCompletedBy: "",
    disposalCompletedDate: new Date(),
  });
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkAssignData, setBulkAssignData] = useState({
    ownerId: "",
    ownerEmail: "",
    ownerName: "",
    secondaryOwnerId: "",
    secondaryOwnerEmail: "",
    secondaryOwnerName: "",
  });

  const fetchEntraidUsers = useCallback(async (configId) => {
    if (!configId) return;
    try {
      setLoadingUsers(true);
      const response = await api.get("/api/asset-register/users/entraid", {
        params: { configId },
      });
      setEntraidUsers(response.data.users || []);
      // Clear any previous errors if successful
      setError((prevError) => {
        if (prevError && prevError.includes("EntraID")) {
          return "";
        }
        return prevError;
      });
    } catch (error) {
      console.warn("Failed to fetch EntraID users:", error);
      // Don't set a blocking error - just log it and continue
      // Users can still manually enter email addresses
      setEntraidUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchAzureConfigs = useCallback(async () => {
    try {
      const response = await api.get("/api/azure-tenant/list");
      const configs = response.data.configs || [];
      setAzureConfigs(configs);
      const validConfigs = configs.filter(
        (c) => c.is_active && c.test_status === "success",
      );
      const selectedStillValid = validConfigs.some(
        (c) => c.id === selectedConfigId,
      );
      if (selectedConfigId && !selectedStillValid) {
        setSelectedConfigId(null);
      }
      // Auto-select most recently tested successful config
      const activeConfig = [...validConfigs].sort((a, b) => {
        const aTime = a.last_tested_at
          ? new Date(a.last_tested_at).getTime()
          : 0;
        const bTime = b.last_tested_at
          ? new Date(b.last_tested_at).getTime()
          : 0;
        return bTime - aTime;
      })[0];
      if (activeConfig && (!selectedConfigId || !selectedStillValid)) {
        setSelectedConfigId(activeConfig.id);
        fetchEntraidUsers(activeConfig.id);
      }
    } catch (error) {
      console.error("Failed to fetch Azure configs:", error);
    }
  }, [selectedConfigId, fetchEntraidUsers]);

  const fetchAssetsWithParams = useCallback(
    async (paramsOverride = {}) => {
      try {
        setLoading(true);
        setError("");

        // Use override values if provided, otherwise use current state
        const page =
          paramsOverride.page !== undefined
            ? paramsOverride.page
            : pagination.page;
        const limit =
          paramsOverride.limit !== undefined
            ? paramsOverride.limit
            : pagination.pageSize;
        const search =
          paramsOverride.search !== undefined
            ? paramsOverride.search
            : filters.search && filters.search.trim() !== ""
              ? filters.search.trim()
              : undefined;
        const deviceType =
          paramsOverride.deviceType !== undefined
            ? paramsOverride.deviceType
            : filters.deviceType || undefined;
        const status =
          paramsOverride.status !== undefined
            ? paramsOverride.status
            : filters.status || undefined;

        const params = {
          page: page + 1,
          limit: limit,
          search: search,
          deviceType: deviceType,
          status: status,
        };

        // Add assetType filter if specified
        if (filters.assetType) {
          params.assetType = filters.assetType;
        }

        const response = await api.get("/api/asset-register", { params });
        setAssets(response.data.assets || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
        }));
      } catch (error) {
        console.error("Failed to fetch assets:", error);
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to load asset register";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.page, pagination.pageSize],
  );

  const fetchAssets = useCallback(() => {
    fetchAssetsWithParams();
  }, [fetchAssetsWithParams]);

  // Load saved column widths
  const loadColumnWidths = async () => {
    try {
      const response = await api.get(
        "/api/user-preferences/asset_register_column_widths",
      );
      if (response.data.value) {
        setColumnWidths(response.data.value);
      }
    } catch (error) {
      console.error("Failed to load column widths:", error);
    }
  };

  // Save column widths
  const saveColumnWidths = async (widths) => {
    try {
      await api.post("/api/user-preferences/asset_register_column_widths", {
        value: widths,
      });
    } catch (error) {
      console.error("Failed to save column widths:", error);
    }
  };

  useEffect(() => {
    fetchAzureConfigs();
    loadColumnWidths();
  }, [fetchAzureConfigs]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleConfigChange = (configId) => {
    setSelectedConfigId(configId ? parseInt(configId) : null);
    if (configId) {
      fetchEntraidUsers(parseInt(configId));
    }
  };

  const handleSyncAzure = async () => {
    if (!selectedConfigId) {
      setError("Please select an Azure tenant configuration first");
      return;
    }

    try {
      setSyncingAzure(true);
      setError("");
      setSuccess("");
      const response = await api.post("/api/asset-register/sync-azure", {
        configId: selectedConfigId,
      });

      // Handle new response structure with azure and devices
      const azureData = response.data.azure || response.data;
      const devicesData = response.data.devices || {
        total: 0,
        created: 0,
        updated: 0,
        skipped: 0,
      };

      const { skipped, total, errors, metadata } = response.data;
      const azureCreated = azureData.created || 0;
      const azureUpdated = azureData.updated || 0;
      const devicesCreated = devicesData.created || 0;
      const devicesUpdated = devicesData.updated || 0;

      const syncConfig = response.data.config;
      let message = syncConfig
        ? `Sync completed using "${syncConfig.name}" (Client ID: ${syncConfig.clientId}): ${total || (azureData.total || 0) + (devicesData.total || 0)} total`
        : `Sync completed: ${total || (azureData.total || 0) + (devicesData.total || 0)} total`;
      if (azureCreated > 0 || azureUpdated > 0) {
        message += `\nAzure: ${azureCreated} created, ${azureUpdated} updated`;
      }
      if (devicesCreated > 0 || devicesUpdated > 0) {
        message += `\nDevices: ${devicesCreated} created, ${devicesUpdated} updated`;
      }
      if (devicesData.removed > 0) {
        message += `\nDevices: ${devicesData.removed} stale Entra entries removed`;
      }
      if (skipped > 0) {
        message += `, ${skipped} skipped`;
      }

      // Add metadata information
      if (azureData.metadata) {
        message += `\nSubscriptions found: ${azureData.metadata.subscriptionsFound || 0}, processed: ${azureData.metadata.subscriptionsProcessed || 0}`;
        if (azureData.metadata.resourcesPerSubscription) {
          const resourceCounts = Object.entries(
            azureData.metadata.resourcesPerSubscription,
          )
            .map(([subId, count]) => `${subId.substring(0, 8)}...: ${count}`)
            .join(", ");
          if (resourceCounts) {
            message += `\nResources per subscription: ${resourceCounts}`;
          }
        }
      }

      // Show managed vs registered device breakdown
      if (devicesData.managed || devicesData.registered) {
        const managed = devicesData.managed || {};
        const registered = devicesData.registered || {};
        message += `\n  - Managed devices: ${managed.created || 0} created, ${managed.updated || 0} updated (${managed.metadata?.devicesFound || 0} found)`;
        message += `\n  - Unmanaged (BYOD): ${registered.created || 0} created, ${registered.updated || 0} updated (${registered.metadata?.devicesFound || 0} found)`;

        if (managed.error) {
          message += `\n⚠️ Managed devices sync error: ${managed.error}`;
        }
        if (registered.error) {
          message += `\n⚠️ Unmanaged (BYOD) devices sync error: ${registered.error}`;
        }
      } else if (
        devicesData.metadata &&
        devicesData.metadata.devicesFound > 0
      ) {
        message += `\nDevices found: ${devicesData.metadata.devicesFound}`;
      }

      // Show device sync errors if any
      if (devicesData.error) {
        message += `\n⚠️ Device sync error: ${devicesData.error}`;
        if (devicesData.error.includes("DeviceManagement.Read.All")) {
          message +=
            "\n\nTo sync EntraID devices, you need to:\n1. Add DeviceManagement.Read.All permission in Azure AD\n2. Grant admin consent\n3. Wait 2-3 minutes for propagation";
        }
      }

      if (errors && errors.length > 0) {
        const errorDetails = errors
          .map((e) => `${e.resourceName}: ${e.error}`)
          .join("; ");
        setError(`Some resources failed to sync: ${errorDetails}\n${message}`);
        console.error("Sync errors:", errors);
        console.log("Sync metadata:", metadata);
      } else if (devicesData.error && devicesData.total === 0) {
        // Show warning if device sync failed but don't treat as error
        setError(message);
        console.warn("Device sync failed:", devicesData.error);
      } else {
        setSuccess(message);
        console.log("Sync metadata:", metadata);
        if (devicesData.error) {
          console.warn(
            "Device sync had errors but some devices may have been synced:",
            devicesData.error,
          );
        }
      }

      fetchAssets();
    } catch (error) {
      console.error("Failed to sync Azure resources:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to sync Azure resources",
      );
    } finally {
      setSyncingAzure(false);
    }
  };

  const handleSyncSharePoint = async () => {
    if (!selectedConfigId) {
      setError("Please select an Azure tenant configuration first");
      return;
    }

    try {
      setSyncingSharePoint(true);
      setError("");
      setSuccess("");
      const response = await api.post("/api/asset-register/sync-sharepoint", {
        configId: selectedConfigId,
      });

      const { created, updated, skipped, total, errors, metadata } =
        response.data;

      let message = `SharePoint sites synced: ${total} total, ${created} created, ${updated} updated`;
      if (skipped > 0) {
        message += `, ${skipped} skipped`;
      }

      // Add metadata information
      if (metadata) {
        message += `\nSites found: ${metadata.sitesFound || 0}`;
      }

      if (errors && errors.length > 0) {
        const errorDetails = errors
          .map((e) => `${e.siteName}: ${e.error}`)
          .join("; ");
        setError(`Some sites failed to sync: ${errorDetails}\n${message}`);
        console.error("Sync errors:", errors);
      } else {
        setSuccess(message);
      }

      fetchAssets();
    } catch (error) {
      console.error("Failed to sync SharePoint sites:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to sync SharePoint sites",
      );
    } finally {
      setSyncingSharePoint(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") {
      return "£0";
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return "£0";
    }
    return `£${numValue.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "returned":
        return "info";
      case "lost":
        return "error";
      case "damaged":
        return "warning";
      default:
        return "default";
    }
  };

  const fetchAllocationHistory = useCallback(async (assetId) => {
    try {
      setLoadingHistory(true);
      const response = await api.get(
        `/api/asset-register/${assetId}/allocation-history`,
      );
      setAllocationHistory(response.data.history || []);
    } catch (error) {
      console.error("Failed to fetch allocation history:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const handleOpenForm = useCallback(
    (asset = null) => {
      setFormError(""); // Clear any previous form errors
      if (asset) {
        setSelectedAsset(asset);
        fetchAllocationHistory(asset.id);
        setFormData({
          assetTag: asset.asset_tag || "",
          employeeId: asset.employee_id,
          employeeEmail: asset.employee_email,
          employeeName: asset.employee_name,
          employeeIdSecondary: asset.employee_id_secondary || "",
          employeeEmailSecondary: asset.employee_email_secondary || "",
          employeeNameSecondary: asset.employee_name_secondary || "",
          deviceType: asset.is_sharepoint_site
            ? "SharePoint Site"
            : asset.device_type,
          osType: asset.is_sharepoint_site ? "N/A" : asset.os_type,
          serialNumber: asset.serial_number || "",
          allocatedDate: asset.allocated_date
            ? new Date(asset.allocated_date)
            : new Date(),
          cost: asset.cost || "",
          includesVat:
            asset.includes_vat !== undefined
              ? Boolean(asset.includes_vat)
              : true,
          vatRate: asset.vat_rate || 20.0,
          notes: asset.notes || "",
          status: asset.status || "active",
          classification: asset.classification || "Internal",
          lifecycleStage: asset.lifecycle_stage || null,
          // Azure-specific fields
          assetId: asset.asset_id || "",
          assetName: asset.asset_name || "",
          assetType: asset.asset_type || "",
          assetTypeFriendly: asset.asset_type_friendly || "",
          location: asset.location || "",
          azureResourceGroup: asset.azure_resource_group || "",
          azureSubscriptionId: asset.azure_subscription_id || "",
          azureCreatedAt: asset.azure_created_at
            ? new Date(asset.azure_created_at)
            : null,
          // SharePoint-specific fields
          sharepointSiteCollection: asset.sharepoint_site_collection || "",
          sharepointCreatedAt: asset.sharepoint_created_at
            ? new Date(asset.sharepoint_created_at)
            : null,
          sharepointSizeBytes: asset.sharepoint_size_bytes || null,
        });
      } else {
        setSelectedAsset(null);
        setFormData({
          assetTag: "",
          employeeId: "",
          employeeEmail: "",
          employeeName: "",
          employeeIdSecondary: "",
          employeeEmailSecondary: "",
          employeeNameSecondary: "",
          deviceType: "Laptop",
          osType: "Windows",
          serialNumber: "",
          allocatedDate: new Date(),
          cost: "",
          includesVat: true,
          vatRate: 20.0,
          notes: "",
          status: "active",
          classification: "Internal",
          lifecycleStage: null,
          // Azure-specific fields
          assetId: "",
          assetName: "",
          assetType: "",
          assetTypeFriendly: "",
          location: "",
          azureResourceGroup: "",
          azureSubscriptionId: "",
          azureCreatedAt: null,
          // SharePoint-specific fields
          sharepointSiteCollection: "",
          sharepointCreatedAt: null,
          sharepointSizeBytes: null,
        });
      }
      setFormErrors({});
      setOpenForm(true);
    },
    [fetchAllocationHistory],
  );

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedAsset(null);
    setAllocationHistory([]);
    setFormData({
      assetTag: "",
      employeeId: "",
      employeeEmail: "",
      employeeName: "",
      employeeIdSecondary: "",
      employeeEmailSecondary: "",
      employeeNameSecondary: "",
      deviceType: "Laptop",
      osType: "Windows",
      serialNumber: "",
      allocatedDate: new Date(),
      cost: "",
      includesVat: true,
      vatRate: 20.0,
      notes: "",
      status: "active",
      classification: "Internal",
      lifecycleStage: null,
      assetId: "",
      assetName: "",
      assetType: "",
      assetTypeFriendly: "",
      location: "",
      azureResourceGroup: "",
      azureSubscriptionId: "",
      azureCreatedAt: null,
      sharepointSiteCollection: "",
      sharepointCreatedAt: null,
      sharepointSizeBytes: null,
    });
    setFormErrors({});
    setFormError("");
    setReallocateDialogOpen(false);
    setDisposeDialogOpen(false);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.employeeId) errors.employeeId = "Asset Owner is required";

    // Only validate hardware-specific fields for non-Azure resources
    if (!selectedAsset?.is_azure_resource) {
      if (!formData.deviceType) errors.deviceType = "Device type is required";
      if (!formData.osType) errors.osType = "OS type is required";
      if (!formData.allocatedDate)
        errors.allocatedDate = "Allocated date is required";
      // Cost is optional - only validate if provided
      if (
        formData.cost &&
        formData.cost !== "" &&
        parseFloat(formData.cost) < 0
      ) {
        errors.cost = "Cost must be a positive number";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      // Show validation errors in the modal
      setFormError("Please fix the errors below before saving.");
      return;
    }

    try {
      setSaving(true);
      setFormError(""); // Clear any previous errors

      const payload = {
        ...formData,
        allocatedDate: formData.allocatedDate.toISOString(),
      };

      if (selectedAsset) {
        await api.put(`/api/asset-register/${selectedAsset.id}`, payload);
      } else {
        await api.post("/api/asset-register", payload);
      }

      handleCloseForm();
      fetchAssets();
    } catch (error) {
      console.error("Failed to save asset:", error);
      // Show error in the modal, not on the page
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to save asset. Please try again.";
      setFormError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Are you sure you want to delete this asset?")) {
        return;
      }

      try {
        await api.delete(`/api/asset-register/${id}`);
        fetchAssets();
      } catch (error) {
        console.error("Failed to delete asset:", error);
        setError(error.response?.data?.error || "Failed to delete asset");
      }
    },
    [fetchAssets],
  );

  const handleReallocate = async () => {
    if (!selectedAsset) return;
    if (!reallocateData.employeeId) {
      setFormError("Please select a new owner");
      return;
    }

    try {
      setSaving(true);
      await api.post(
        `/api/asset-register/${selectedAsset.id}/reallocate`,
        reallocateData,
      );
      setReallocateDialogOpen(false);
      setReallocateData({
        employeeId: "",
        employeeEmail: "",
        employeeName: "",
        reason: "",
        notes: "",
      });
      await fetchAllocationHistory(selectedAsset.id);
      fetchAssets();
      // Refresh selected asset
      const updatedAssets = await api.get("/api/asset-register");
      const updated = updatedAssets.data.assets.find(
        (a) => a.id === selectedAsset.id,
      );
      if (updated) {
        setSelectedAsset(updated);
        handleOpenForm(updated);
      }
    } catch (error) {
      console.error("Failed to reallocate asset:", error);
      setFormError(error.response?.data?.error || "Failed to reallocate asset");
    } finally {
      setSaving(false);
    }
  };

  const handleDispose = async () => {
    if (!selectedAsset) return;
    if (!disposeData.disposalReason) {
      setFormError("Please provide disposal reason");
      return;
    }

    try {
      setSaving(true);
      // Convert date to ISO string if provided
      const payload = {
        ...disposeData,
        disposalCompletedDate: disposeData.disposalCompletedDate
          ? disposeData.disposalCompletedDate.toISOString()
          : null,
      };
      await api.post(
        `/api/asset-register/${selectedAsset.id}/dispose`,
        payload,
      );
      setDisposeDialogOpen(false);
      setDisposeData({
        disposalReason: "",
        disposalNotes: "",
        dataWipeMethod: "",
        disposalCertificate: "",
        disposalCompletedBy: "",
        disposalCompletedDate: new Date(),
      });
      await fetchAllocationHistory(selectedAsset.id);
      fetchAssets();
      // Refresh selected asset to get updated status
      const updatedAssets = await api.get("/api/asset-register");
      const updated = updatedAssets.data.assets.find(
        (a) => a.id === selectedAsset.id,
      );
      if (updated) {
        setSelectedAsset(updated);
        // Update formData to reflect disposed status and other disposal fields
        setFormData({
          ...formData,
          status: "disposed",
        });
        // Keep the form open so user can see the updated status
      } else {
        // Asset might be filtered out, just close the form
        handleCloseForm();
      }
    } catch (error) {
      console.error("Failed to dispose asset:", error);
      setFormError(error.response?.data?.error || "Failed to dispose asset");
    } finally {
      setSaving(false);
    }
  };

  const calculateVAT = () => {
    if (!formData.cost || parseFloat(formData.cost) <= 0) {
      return { net: 0, vat: 0, total: 0 };
    }
    const cost = parseFloat(formData.cost);
    const vatRate = parseFloat(formData.vatRate) || 20.0;

    let net, vat;
    if (formData.includesVat) {
      net = cost / (1 + vatRate / 100);
      vat = cost - net;
    } else {
      net = cost;
      vat = (cost * vatRate) / 100;
    }

    return {
      net: parseFloat(net.toFixed(2)),
      vat: parseFloat(vat.toFixed(2)),
      total: formData.includesVat ? cost : net + vat,
    };
  };

  const vatBreakdown = calculateVAT();

  const handleBulkAssign = async () => {
    if (selectedRows.length === 0) {
      setError("Please select at least one asset");
      return;
    }

    if (!bulkAssignData.ownerId && !bulkAssignData.secondaryOwnerId) {
      setFormError("Please select at least one owner (Primary or Secondary)");
      return;
    }

    try {
      setSaving(true);
      setFormError("");

      await api.post("/api/asset-register/bulk-assign", {
        assetIds: selectedRows,
        ownerId: bulkAssignData.ownerId || null,
        ownerEmail: bulkAssignData.ownerEmail || null,
        ownerName: bulkAssignData.ownerName || null,
        secondaryOwnerId: bulkAssignData.secondaryOwnerId || null,
        secondaryOwnerEmail: bulkAssignData.secondaryOwnerEmail || null,
        secondaryOwnerName: bulkAssignData.secondaryOwnerName || null,
      });

      setBulkAssignDialogOpen(false);
      setSelectedRows([]);
      setBulkAssignData({
        ownerId: "",
        ownerEmail: "",
        ownerName: "",
        secondaryOwnerId: "",
        secondaryOwnerEmail: "",
        secondaryOwnerName: "",
      });
      setSuccess(
        `Successfully assigned owners to ${selectedRows.length} asset(s)`,
      );
      fetchAssets();
    } catch (error) {
      console.error("Failed to bulk assign owners:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to assign owners. Please try again.";
      setFormError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () =>
      [
        {
          field: "is_azure_resource",
          headerName: "Type",
          width: isMobile ? 80 : 100,
          minWidth: 80,
          flex: isMobile ? 0 : 0.5,
          renderCell: (params) => {
            if (params.row.is_sharepoint_site) {
              return <Chip label="SharePoint" color="secondary" size="small" />;
            }
            // EntraID devices are treated as Hardware
            if (params.row.is_azure_resource) {
              return <Chip label="Azure" color="info" size="small" />;
            }
            return <Chip label="Hardware" color="default" size="small" />;
          },
        },
        {
          field: "azure_resource_group",
          headerName: "Resource Group",
          width: isMobile ? 120 : 180,
          minWidth: 120,
          flex: isMobile ? 0.6 : 0.8,
          hide: isMobile, // Hide on mobile to save space
          renderCell: (params) => params.value || "N/A",
        },
        {
          field: "asset_name",
          headerName: "Asset Name",
          width: isMobile ? 150 : 200,
          minWidth: 150,
          flex: 1.2,
          renderCell: (params) =>
            params.value || params.row.employee_name || "N/A",
        },
        {
          field: "asset_type",
          headerName: "Asset Type",
          width: isMobile ? 120 : 180,
          minWidth: 120,
          flex: isMobile ? 0.8 : 0.9,
          renderCell: (params) => {
            // For SharePoint sites
            if (params.row.is_sharepoint_site) {
              return "SharePoint Site";
            }
            // For Azure resources, show friendly type if available, otherwise show device_type
            if (params.row.is_azure_resource) {
              return (
                params.row.asset_type_friendly ||
                params.value ||
                "Azure Resource"
              );
            }
            return params.value || params.row.device_type || "N/A";
          },
        },
        {
          field: "is_managed_device",
          headerName: "Device Status",
          width: isMobile ? 100 : 140,
          minWidth: 100,
          flex: isMobile ? 0.6 : 0.7,
          hide: isMobile, // Hide on mobile to save space
          renderCell: (params) => {
            // Only show for EntraID devices
            if (!params.row.is_entraid_device) {
              return null;
            }
            // Handle both boolean and numeric values (MySQL returns 1/0 for BOOLEAN)
            const isManaged =
              params.row.is_managed_device === true ||
              params.row.is_managed_device === 1;
            const isUnmanaged =
              params.row.is_managed_device === false ||
              params.row.is_managed_device === 0;

            if (isManaged) {
              return <Chip label="Managed" color="success" size="small" />;
            } else if (isUnmanaged) {
              return (
                <Chip label="Unmanaged (BYOD)" color="warning" size="small" />
              );
            }
            // If NULL, assume managed (for backward compatibility)
            return <Chip label="Managed" color="success" size="small" />;
          },
        },
        {
          field: "employee_name",
          headerName: "Asset Owner",
          width: isMobile ? 120 : 180,
          minWidth: 120,
          flex: isMobile ? 0.8 : 0.9,
          hide: isMobile, // Hide on mobile
        },
        {
          field: "os_type",
          headerName: "OS Type",
          width: isMobile ? 80 : 120,
          minWidth: 80,
          flex: isMobile ? 0 : 0.7,
          hide: isMobile, // Hide on mobile
          renderCell: (params) => {
            if (params.row.is_azure_resource) return "N/A";
            return params.value || "N/A";
          },
        },
        {
          field: "allocated_date",
          headerName: isMobile ? "Date" : "Allocated/Created Date",
          width: isMobile ? 100 : 180,
          minWidth: 100,
          flex: isMobile ? 0.7 : 0.9,
          renderCell: (params) => {
            // For SharePoint sites, show SharePoint creation date if available
            if (
              params.row.is_sharepoint_site &&
              params.row.sharepoint_created_at
            ) {
              return format(
                new Date(params.row.sharepoint_created_at),
                "dd/MM/yyyy HH:mm",
              );
            }
            // For Azure resources, show Azure creation date if available, otherwise allocated_date
            if (params.row.is_azure_resource && params.row.azure_created_at) {
              return format(
                new Date(params.row.azure_created_at),
                "dd/MM/yyyy HH:mm",
              );
            }
            return params.value
              ? format(new Date(params.value), "dd/MM/yyyy HH:mm")
              : "";
          },
        },
        {
          field: "status",
          headerName: "Status",
          width: isMobile ? 80 : 120,
          minWidth: 80,
          flex: isMobile ? 0.5 : 0.7,
          renderCell: (params) => (
            <Chip
              label={params.value}
              color={getStatusColor(params.value)}
              size="small"
            />
          ),
        },
        {
          field: "classification",
          headerName: "Classification",
          width: isMobile ? 100 : 140,
          minWidth: 100,
          flex: isMobile ? 0.6 : 0.8,
          hide: isMobile, // Hide on mobile to save space
          renderCell: (params) => {
            const colorMap = {
              Public: "default",
              Internal: "primary",
              Confidential: "error",
              External: "warning",
              BYOD: "warning",
            };
            return (
              <Chip
                label={params.value}
                color={colorMap[params.value] || "default"}
                size="small"
              />
            );
          },
        },
        {
          field: "sharepoint_size_bytes",
          headerName: "Size",
          width: isMobile ? 80 : 120,
          minWidth: 80,
          flex: isMobile ? 0 : 0.7,
          hide: isMobile || isTablet, // Hide on mobile and tablet
          renderCell: (params) => {
            // Only show size for SharePoint sites
            if (!params.row.is_sharepoint_site || !params.value) return "N/A";

            // Format bytes to human-readable format
            const bytes = parseInt(params.value, 10);
            if (isNaN(bytes)) return "N/A";

            const units = ["B", "KB", "MB", "GB", "TB"];
            let size = bytes;
            let unitIndex = 0;

            while (size >= 1024 && unitIndex < units.length - 1) {
              size /= 1024;
              unitIndex++;
            }

            return `${size.toFixed(2)} ${units[unitIndex]}`;
          },
        },
        {
          field: "lifecycle_stage",
          headerName: "Lifecycle Stage",
          width: 140,
          flex: 0.8,
          renderCell: (params) => {
            if (!params.value) return "N/A";
            const colorMap = {
              Production: "success",
              Development: "info",
              Staging: "warning",
              Test: "default",
              Disposed: "error",
            };
            return (
              <Chip
                label={params.value}
                color={colorMap[params.value] || "default"}
                size="small"
              />
            );
          },
        },
        {
          field: "actions",
          headerName: "Actions",
          width: isMobile ? 80 : 120,
          minWidth: 80,
          flex: isMobile ? 0 : 0.5,
          sortable: false,
          renderCell: (params) => (
            <Box>
              <IconButton
                size="small"
                onClick={() => handleOpenForm(params.row)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(params.row.id)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ),
        },
      ].map((col) => {
        if (col.field !== "actions") {
          return col;
        }
        return {
          ...col,
          width: isMobile ? 100 : 120,
          minWidth: isMobile ? 100 : 120,
          maxWidth: isMobile ? 100 : 120,
          flex: 0,
        };
      }),
    [isMobile, isTablet, handleDelete, handleOpenForm],
  );

  const gridColumns = useMemo(
    () => applyPersistedColumnWidths(columns, columnWidths),
    [columns, columnWidths],
  );

  return (
      <Box width="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          gap={2}
        >
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              Add Asset
            </Button>
            {selectedRows.length > 0 && (
              <Button
                variant="outlined"
                onClick={() => setBulkAssignDialogOpen(true)}
              >
                Assign Owners ({selectedRows.length})
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Azure Configuration Selector */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Azure Tenant Configuration</InputLabel>
                  <Select
                    value={selectedConfigId || ""}
                    label="Azure Tenant Configuration"
                    onChange={(e) => handleConfigChange(e.target.value)}
                  >
                    <MenuItem value="">Select configuration...</MenuItem>
                    {azureConfigs
                      .filter((c) => c.is_active && c.test_status === "success")
                      .map((config) => (
                        <MenuItem key={config.id} value={config.id}>
                          {config.name} ({config.client_id})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                {selectedConfigId && (
                  <>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={
                        syncingAzure ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudSyncIcon />
                        )
                      }
                      onClick={handleSyncAzure}
                      disabled={syncingAzure}
                    >
                      {syncingAzure ? "Syncing..." : "Sync Azure Resources"}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={
                        syncingSharePoint ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudSyncIcon />
                        )
                      }
                      onClick={handleSyncSharePoint}
                      disabled={syncingSharePoint}
                      sx={{ mt: 1 }}
                    >
                      {syncingSharePoint
                        ? "Syncing..."
                        : "Sync SharePoint Sites"}
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} sx={{ flexWrap: "nowrap" }}>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  minWidth: { xs: "100%", sm: "200px" },
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                }}
              >
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  placeholder="Search by asset owner name, email..."
                  size="small"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  minWidth: { xs: "100%", sm: "150px" },
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Asset Type</InputLabel>
                  <Select
                    value={filters.assetType}
                    label="Asset Type"
                    onChange={(e) =>
                      handleFilterChange("assetType", e.target.value)
                    }
                  >
                    <MenuItem value="">All Assets</MenuItem>
                    <MenuItem value="hardware">Hardware Assets</MenuItem>
                    <MenuItem value="azure">Azure Resources</MenuItem>
                    <MenuItem value="sharepoint">SharePoint Sites</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  minWidth: { xs: "100%", sm: "150px" },
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Device Type</InputLabel>
                  <Select
                    value={filters.deviceType}
                    label="Device Type"
                    onChange={(e) =>
                      handleFilterChange("deviceType", e.target.value)
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Laptop">Laptop</MenuItem>
                    <MenuItem value="Monitor">Monitor</MenuItem>
                    <MenuItem value="Mobile Phone">Mobile Phone</MenuItem>
                    <MenuItem value="Tablet">Tablet</MenuItem>
                    <MenuItem value="Keyboard & Mouse">
                      Keyboard & Mouse
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  minWidth: { xs: "100%", sm: "150px" },
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="returned">Returned</MenuItem>
                    <MenuItem value="lost">Lost</MenuItem>
                    <MenuItem value="damaged">Damaged</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Data Grid */}
        <Card sx={{ width: "100%" }}>
          <CardContent
            sx={{
              p: { xs: 1, sm: 2 },
              overflow: "hidden",
              width: "100%",
              "&:last-child": { pb: 2 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: {
                  xs: "calc(100vh - 420px)",
                  sm: "calc(100vh - 380px)",
                },
                minHeight: 400,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DataGrid
                rows={assets}
                columns={gridColumns}
                loading={loading}
                pagination
                paginationMode="server"
                rowCount={pagination.total}
                paginationModel={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                onPaginationModelChange={(newModel) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: newModel.page,
                    pageSize: newModel.pageSize,
                  }));
                }}
                checkboxSelection
                disableSelectionOnClick
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={(newSelection) => {
                  setSelectedRows(newSelection);
                }}
                getRowId={(row) => row.id}
                disableColumnResize={false}
                columnHeaderHeight={isMobile ? 40 : 56}
                rowHeight={isMobile ? 40 : 52}
                onColumnWidthChange={(params) => {
                  const field = params.colDef?.field;
                  const width = params.width;
                  setColumnWidths((prev) => {
                    const updated = mergeColumnWidth(prev, field, width);
                    if (updated === prev) {
                      return prev;
                    }
                    if (columnWidthSaveTimeoutRef.current) {
                      clearTimeout(columnWidthSaveTimeoutRef.current);
                    }
                    columnWidthSaveTimeoutRef.current = setTimeout(() => {
                      saveColumnWidths(updated);
                    }, 1000);
                    return updated;
                  });
                }}
                initialState={{
                  sorting: {
                    sortModel: [],
                  },
                  filter: {
                    filterModel: {
                      items: [],
                    },
                  },
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  border: 0,
                  "& .MuiDataGrid-main": {
                    width: "100%",
                    overflow: "auto",
                  },
                  "& .MuiDataGrid-container--top [role=row]": {
                    maxWidth: "100%",
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    padding: { xs: "4px 8px", sm: "8px 16px" },
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    padding: { xs: "4px 8px", sm: "8px 16px" },
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                  },
                  "& .MuiDataGrid-root": {
                    border: "none",
                    width: "100%",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    width: "100%",
                  },
                  "& .MuiDataGrid-virtualScrollerContent": {
                    width: "100%",
                  },
                  // Make column separators visible and clickable for resizing
                  "& .MuiDataGrid-columnSeparator": {
                    visibility: "visible !important",
                    display: "block !important",
                    pointerEvents: "auto !important",
                    cursor: "col-resize !important",
                    zIndex: 100,
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.1)",
                    },
                  },
                  // Highlight selected rows
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "rgba(25, 118, 210, 0.08) !important",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.12) !important",
                    },
                  },
                  "& .MuiDataGrid-row.Mui-selected:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.12) !important",
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openForm}
          onClose={handleCloseForm}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <Typography variant="h6" component="span">
                {selectedAsset ? "Edit Asset" : "Add New Asset"}
              </Typography>
              {selectedAsset?.is_azure_resource === true && (
                <Chip
                  label="Azure Resource"
                  color="info"
                  size="small"
                  sx={{ ml: 2 }}
                />
              )}
              {selectedAsset?.is_sharepoint_site === true && (
                <Chip
                  label="SharePoint Site"
                  color="secondary"
                  size="small"
                  sx={{ ml: 2 }}
                />
              )}
              {selectedAsset?.is_entraid_device &&
                (selectedAsset?.is_managed_device === true ? (
                  <Chip
                    label="Managed"
                    color="success"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                ) : selectedAsset?.is_managed_device === false ? (
                  <Chip
                    label="Unmanaged (BYOD)"
                    color="warning"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                ) : null)}
            </Box>
          </DialogTitle>
          <DialogContent>
            {formError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setFormError("")}
              >
                {formError}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Show Azure resource info if editing Azure resource */}
              {selectedAsset?.is_azure_resource === true && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      This is an Azure resource. Some fields are synced from
                      Azure and cannot be edited.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Resource Group"
                      value={
                        formData.azureResourceGroup ||
                        selectedAsset?.azure_resource_group ||
                        ""
                      }
                      disabled
                      helperText="Synced from Azure"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Asset Name"
                      value={
                        formData.assetName || selectedAsset?.asset_name || ""
                      }
                      disabled
                      helperText="Synced from Azure"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Asset Type"
                      value={
                        formData.assetTypeFriendly ||
                        selectedAsset?.asset_type_friendly ||
                        formData.assetType ||
                        selectedAsset?.asset_type ||
                        ""
                      }
                      disabled
                      helperText="Synced from Azure"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={formData.location || selectedAsset?.location || ""}
                      disabled
                      helperText="Synced from Azure"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Asset ID"
                      value={formData.assetId || selectedAsset?.asset_id || ""}
                      disabled
                      helperText="Azure Resource ID"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Azure Created Date"
                      value={
                        formData.azureCreatedAt
                          ? format(formData.azureCreatedAt, "dd/MM/yyyy HH:mm")
                          : selectedAsset?.azure_created_at
                            ? format(
                                new Date(selectedAsset.azure_created_at),
                                "dd/MM/yyyy HH:mm",
                              )
                            : "N/A"
                      }
                      disabled
                      helperText="Date resource was created in Azure"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subscription ID"
                      value={
                        formData.azureSubscriptionId ||
                        selectedAsset?.azure_subscription_id ||
                        ""
                      }
                      disabled
                      helperText="Azure Subscription ID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      value={
                        formData.serialNumber ||
                        selectedAsset?.serial_number ||
                        ""
                      }
                      disabled
                      helperText="Azure Resource ID (used as serial number)"
                      multiline
                      rows={2}
                    />
                  </Grid>
                </>
              )}

              {/* Show SharePoint site info if editing SharePoint site */}
              {selectedAsset?.is_sharepoint_site === true && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      This is a SharePoint site. Some fields are synced from
                      SharePoint and cannot be edited.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Site Collection"
                      value={
                        formData.sharepointSiteCollection ||
                        selectedAsset?.sharepoint_site_collection ||
                        ""
                      }
                      disabled
                      helperText="Synced from SharePoint"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Asset Name"
                      value={
                        formData.assetName || selectedAsset?.asset_name || ""
                      }
                      disabled
                      helperText="Synced from SharePoint"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Site URL"
                      value={formData.location || selectedAsset?.location || ""}
                      disabled
                      helperText="SharePoint Site URL"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Site ID"
                      value={formData.assetId || selectedAsset?.asset_id || ""}
                      disabled
                      helperText="SharePoint Site ID"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SharePoint Created Date"
                      value={
                        formData.sharepointCreatedAt
                          ? format(
                              formData.sharepointCreatedAt,
                              "dd/MM/yyyy HH:mm",
                            )
                          : selectedAsset?.sharepoint_created_at
                            ? format(
                                new Date(selectedAsset.sharepoint_created_at),
                                "dd/MM/yyyy HH:mm",
                              )
                            : "N/A"
                      }
                      disabled
                      helperText="Date site was created in SharePoint"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Site Size"
                      value={(() => {
                        const sizeBytes =
                          formData.sharepointSizeBytes ||
                          selectedAsset?.sharepoint_size_bytes;
                        if (!sizeBytes) return "N/A";
                        const bytes = parseInt(sizeBytes, 10);
                        if (isNaN(bytes)) return "N/A";
                        const units = ["B", "KB", "MB", "GB", "TB"];
                        let size = bytes;
                        let unitIndex = 0;
                        while (size >= 1024 && unitIndex < units.length - 1) {
                          size /= 1024;
                          unitIndex++;
                        }
                        return `${size.toFixed(2)} ${units[unitIndex]}`;
                      })()}
                      disabled
                      helperText="Total storage used by the site"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      value={
                        formData.serialNumber ||
                        selectedAsset?.serial_number ||
                        ""
                      }
                      disabled
                      helperText="SharePoint Site ID (used as serial number)"
                      multiline
                      rows={2}
                    />
                  </Grid>
                </>
              )}

              {/* Show EntraID device info if editing EntraID device */}
              {(selectedAsset?.is_entraid_device === true ||
                selectedAsset?.is_entraid_device === 1) && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      This is an EntraID managed device. Some fields are synced
                      from EntraID/Intune and cannot be edited.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Device Name"
                      value={
                        formData.assetName ||
                        selectedAsset?.entraid_device_name ||
                        selectedAsset?.asset_name ||
                        ""
                      }
                      disabled
                      helperText="Synced from EntraID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Manufacturer"
                      value={selectedAsset?.entraid_manufacturer || ""}
                      disabled
                      helperText="Synced from EntraID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Model"
                      value={selectedAsset?.entraid_model || ""}
                      disabled
                      helperText="Synced from EntraID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="OS Version"
                      value={selectedAsset?.entraid_os_version || ""}
                      disabled
                      helperText="Synced from EntraID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Device Type"
                      value={selectedAsset?.entraid_device_type || ""}
                      disabled
                      helperText="Synced from EntraID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      value={
                        formData.serialNumber ||
                        selectedAsset?.entraid_serial_number ||
                        selectedAsset?.serial_number ||
                        ""
                      }
                      disabled
                      helperText="Synced from EntraID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Device Status"
                      value={(() => {
                        const isManaged =
                          selectedAsset?.is_managed_device === true ||
                          selectedAsset?.is_managed_device === 1;
                        const isUnmanaged =
                          selectedAsset?.is_managed_device === false ||
                          selectedAsset?.is_managed_device === 0;
                        if (isManaged) return "Managed";
                        if (isUnmanaged) return "Unmanaged (BYOD)";
                        return "Managed"; // Default for backward compatibility
                      })()}
                      disabled
                      helperText="Device management status from EntraID/Intune"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Enrolled Date"
                      value={
                        selectedAsset?.entraid_enrolled_date
                          ? format(
                              new Date(selectedAsset.entraid_enrolled_date),
                              "dd/MM/yyyy HH:mm",
                            )
                          : "N/A"
                      }
                      disabled
                      helperText="Date device was enrolled in EntraID/Intune"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Sync Date"
                      value={
                        selectedAsset?.entraid_last_sync_date
                          ? format(
                              new Date(selectedAsset.entraid_last_sync_date),
                              "dd/MM/yyyy HH:mm",
                            )
                          : "N/A"
                      }
                      disabled
                      helperText="Last sync from EntraID/Intune"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Compliance State"
                      value={selectedAsset?.entraid_compliance_state || "N/A"}
                      disabled
                      helperText="Device compliance status"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Device ID"
                      value={selectedAsset?.entraid_device_id || ""}
                      disabled
                      helperText="EntraID managed device ID"
                      multiline
                      rows={2}
                    />
                  </Grid>
                </>
              )}

              {/* Azure Config Selection (only for new hardware assets) */}
              {!selectedAsset?.is_azure_resource && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Azure Tenant Configuration</InputLabel>
                    <Select
                      value={selectedConfigId || ""}
                      label="Azure Tenant Configuration"
                      onChange={(e) => handleConfigChange(e.target.value)}
                    >
                      <MenuItem value="">Select configuration...</MenuItem>
                      {azureConfigs
                        .filter(
                          (c) => c.is_active && c.test_status === "success",
                        )
                        .map((config) => (
                          <MenuItem key={config.id} value={config.id}>
                            {config.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  {selectedConfigId && (
                    <Button
                      size="small"
                      onClick={() => fetchEntraidUsers(selectedConfigId)}
                      disabled={loadingUsers}
                      sx={{ mt: 1 }}
                    >
                      {loadingUsers ? "Loading..." : "Refresh Asset Owners"}
                    </Button>
                  )}
                </Grid>
              )}

              {/* Asset Tag (Unique ID for ISO Compliance) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Asset Tag (Unique ID)"
                  value={formData.assetTag}
                  onChange={(e) =>
                    setFormData({ ...formData, assetTag: e.target.value })
                  }
                  helperText="Unique identifier for ISO compliance tracking (optional)"
                  disabled={
                    !!selectedAsset?.is_azure_resource ||
                    !!selectedAsset?.is_sharepoint_site
                  }
                />
              </Grid>

              {/* Asset Owner Selection */}
              <Grid item xs={12}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find((u) => u.id === formData.employeeId) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData({
                        ...formData,
                        employeeId: newValue.id,
                        employeeEmail: newValue.email,
                        employeeName: newValue.name,
                      });
                    } else {
                      setFormData({
                        ...formData,
                        employeeId: "",
                        employeeEmail: "",
                        employeeName: "",
                      });
                    }
                  }}
                  loading={loadingUsers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Asset Owner"
                      error={!!formErrors.employeeId}
                      helperText={formErrors.employeeId}
                      required
                    />
                  )}
                />
              </Grid>

              {/* Secondary Owner Selection */}
              <Grid item xs={12}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find(
                      (u) => u.id === formData.employeeIdSecondary,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData({
                        ...formData,
                        employeeIdSecondary: newValue.id,
                        employeeEmailSecondary: newValue.email,
                        employeeNameSecondary: newValue.name,
                      });
                    } else {
                      setFormData({
                        ...formData,
                        employeeIdSecondary: "",
                        employeeEmailSecondary: "",
                        employeeNameSecondary: "",
                      });
                    }
                  }}
                  loading={loadingUsers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Secondary Owner (Backup Owner)"
                      helperText="Optional - backup owner for this asset"
                    />
                  )}
                />
              </Grid>

              {/* Asset Name (for all asset types when not synced) */}
              {!selectedAsset?.is_azure_resource &&
                !selectedAsset?.is_sharepoint_site &&
                !selectedAsset?.is_entraid_device && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Asset Name"
                      value={formData.assetName}
                      onChange={(e) =>
                        setFormData({ ...formData, assetName: e.target.value })
                      }
                      helperText="Name or description of the asset"
                    />
                  </Grid>
                )}

              {/* Resource Group (for all asset types when present) */}
              {selectedAsset?.azure_resource_group && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Resource Group"
                    value={
                      formData.azureResourceGroup ||
                      selectedAsset?.azure_resource_group ||
                      ""
                    }
                    disabled={!!selectedAsset?.is_azure_resource}
                    helperText={
                      selectedAsset?.is_azure_resource
                        ? "Synced from Azure"
                        : "Azure Resource Group (if applicable)"
                    }
                  />
                </Grid>
              )}

              {/* Asset Type (only for hardware assets and SharePoint sites) */}
              {!selectedAsset?.is_azure_resource && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Asset Type</InputLabel>
                      <Select
                        value={formData.deviceType}
                        label="Asset Type"
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            deviceType: e.target.value,
                          })
                        }
                        error={!!formErrors.deviceType}
                        required
                        disabled={!!selectedAsset?.is_sharepoint_site}
                      >
                        <MenuItem value="Laptop">Laptop</MenuItem>
                        <MenuItem value="Monitor">Monitor</MenuItem>
                        <MenuItem value="Mobile Phone">Mobile Phone</MenuItem>
                        <MenuItem value="Tablet">Tablet</MenuItem>
                        <MenuItem value="Keyboard & Mouse">
                          Keyboard & Mouse
                        </MenuItem>
                        <MenuItem value="SharePoint Site">
                          SharePoint Site
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* OS Type */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>OS Type</InputLabel>
                      <Select
                        value={formData.osType}
                        label="OS Type"
                        onChange={(e) =>
                          setFormData({ ...formData, osType: e.target.value })
                        }
                        error={!!formErrors.osType}
                        required
                        disabled={!!selectedAsset?.is_sharepoint_site}
                      >
                        <MenuItem value="Apple">Apple</MenuItem>
                        <MenuItem value="Android">Android</MenuItem>
                        <MenuItem value="Windows">Windows</MenuItem>
                        <MenuItem value="N/A">N/A</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Serial Number */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      value={formData.serialNumber}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          serialNumber: e.target.value,
                        })
                      }
                      placeholder="Enter device serial number"
                    />
                  </Grid>
                </>
              )}

              {/* Allocated Date/Time (only for hardware assets) */}
              {!selectedAsset?.is_azure_resource && (
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Allocated Date & Time"
                    value={formData.allocatedDate}
                    onChange={(date) =>
                      setFormData({ ...formData, allocatedDate: date })
                    }
                    textField={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!formErrors.allocatedDate}
                        helperText={formErrors.allocatedDate}
                        required
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Device Status (show if device has managed_device field) */}
              {(selectedAsset?.is_entraid_device === true ||
                selectedAsset?.is_entraid_device === 1 ||
                selectedAsset?.is_managed_device !== undefined) && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Device Status"
                    value={(() => {
                      const isManaged =
                        selectedAsset?.is_managed_device === true ||
                        selectedAsset?.is_managed_device === 1;
                      const isUnmanaged =
                        selectedAsset?.is_managed_device === false ||
                        selectedAsset?.is_managed_device === 0;
                      if (isManaged) return "Managed";
                      if (isUnmanaged) return "Unmanaged (BYOD)";
                      return "N/A";
                    })()}
                    disabled
                    helperText="Device management status from EntraID/Intune"
                  />
                </Grid>
              )}

              {/* Classification */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Classification</InputLabel>
                  <Select
                    value={formData.classification}
                    label="Classification"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        classification: e.target.value,
                      })
                    }
                    required
                  >
                    <MenuItem value="Public">Public</MenuItem>
                    <MenuItem value="Internal">Internal</MenuItem>
                    <MenuItem value="Confidential">Confidential</MenuItem>
                    <MenuItem value="External">External</MenuItem>
                    <MenuItem value="BYOD">
                      BYOD (Bring Your Own Device)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Lifecycle Stage */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Lifecycle Stage</InputLabel>
                  <Select
                    value={formData.lifecycleStage || ""}
                    label="Lifecycle Stage"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        lifecycleStage: e.target.value || null,
                      })
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Production">Production</MenuItem>
                    <MenuItem value="Development">Development</MenuItem>
                    <MenuItem value="Staging">Staging</MenuItem>
                    <MenuItem value="Test">Test</MenuItem>
                    <MenuItem value="Disposed">Disposed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Status (for all assets) */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    required
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="returned">Returned</MenuItem>
                    <MenuItem value="lost">Lost</MenuItem>
                    <MenuItem value="damaged">Damaged</MenuItem>
                    <MenuItem value="disposed">Disposed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Cost (only for hardware assets) */}
              {!selectedAsset?.is_azure_resource && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cost (£)"
                      type="number"
                      value={formData.cost}
                      onChange={(e) =>
                        setFormData({ ...formData, cost: e.target.value })
                      }
                      error={!!formErrors.cost}
                      helperText={formErrors.cost || "Optional"}
                      inputProps={{ step: "0.01", min: "0" }}
                    />
                  </Grid>

                  {/* VAT Rate */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="VAT Rate (%)"
                      type="number"
                      value={formData.vatRate}
                      onChange={(e) =>
                        setFormData({ ...formData, vatRate: e.target.value })
                      }
                      inputProps={{ step: "0.01", min: "0", max: "100" }}
                    />
                  </Grid>

                  {/* Includes VAT */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.includesVat}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              includesVat: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Cost includes VAT"
                    />
                  </Grid>

                  {/* VAT Breakdown */}
                  {formData.cost && parseFloat(formData.cost) > 0 && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            VAT Breakdown:
                          </Typography>
                          <Typography variant="body2">
                            Net: {formatCurrency(vatBreakdown.net)}
                          </Typography>
                          <Typography variant="body2">
                            VAT: {formatCurrency(vatBreakdown.vat)}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            Total: {formatCurrency(vatBreakdown.total)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </>
              )}

              {/* Allocation History (ISO Compliance) - Only show when editing */}
              {selectedAsset && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Allocation History (ISO Compliance)
                    </Typography>
                    {loadingHistory ? (
                      <CircularProgress size={24} />
                    ) : allocationHistory.length > 0 ? (
                      <Box sx={{ mt: 2 }}>
                        {allocationHistory.map((entry, index) => (
                          <Card
                            key={entry.id}
                            variant="outlined"
                            sx={{ mb: 1 }}
                          >
                            <CardContent>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="start"
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                  >
                                    {entry.action.charAt(0).toUpperCase() +
                                      entry.action.slice(1)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {format(
                                      new Date(entry.action_date),
                                      "dd/MM/yyyy HH:mm",
                                    )}
                                  </Typography>
                                  {entry.from_employee_name && (
                                    <Typography variant="body2">
                                      From: {entry.from_employee_name} (
                                      {entry.from_employee_email})
                                    </Typography>
                                  )}
                                  {entry.to_employee_name && (
                                    <Typography variant="body2">
                                      To: {entry.to_employee_name} (
                                      {entry.to_employee_email})
                                    </Typography>
                                  )}
                                  {entry.reason && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      Reason: {entry.reason}
                                    </Typography>
                                  )}
                                  {entry.notes && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      Notes: {entry.notes}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  By: {entry.performed_by_name}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        No allocation history available
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} mt={2}>
                      {selectedAsset.status !== "disposed" && (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setReallocateDialogOpen(true)}
                            disabled={saving}
                          >
                            Reallocate Asset
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setDisposeDialogOpen(true)}
                            disabled={saving}
                          >
                            Dispose Asset
                          </Button>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                </>
              )}

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reallocation Dialog */}
        <Dialog
          open={reallocateDialogOpen}
          onClose={() => setReallocateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reallocate Asset</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Reallocating this asset will create a new entry in the allocation
              history and update the current owner.
            </Alert>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find(
                      (u) => u.id === reallocateData.employeeId,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setReallocateData({
                        ...reallocateData,
                        employeeId: newValue.id,
                        employeeEmail: newValue.email,
                        employeeName: newValue.name,
                      });
                    } else {
                      setReallocateData({
                        ...reallocateData,
                        employeeId: "",
                        employeeEmail: "",
                        employeeName: "",
                      });
                    }
                  }}
                  loading={loadingUsers}
                  renderInput={(params) => (
                    <TextField {...params} label="New Asset Owner" required />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason for Reallocation"
                  multiline
                  rows={2}
                  value={reallocateData.reason}
                  onChange={(e) =>
                    setReallocateData({
                      ...reallocateData,
                      reason: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  multiline
                  rows={2}
                  value={reallocateData.notes}
                  onChange={(e) =>
                    setReallocateData({
                      ...reallocateData,
                      notes: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setReallocateDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReallocate}
              variant="contained"
              disabled={saving}
            >
              {saving ? "Reallocating..." : "Reallocate"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Disposal Dialog */}
        <Dialog
          open={disposeDialogOpen}
          onClose={() => setDisposeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Dispose Asset</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Disposing this asset will mark it as disposed and create a
              permanent record in the allocation history.
            </Alert>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Disposal Reason</InputLabel>
                  <Select
                    value={disposeData.disposalReason}
                    label="Disposal Reason"
                    onChange={(e) =>
                      setDisposeData({
                        ...disposeData,
                        disposalReason: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="End of Life - Recycled">
                      End of Life - Recycled
                    </MenuItem>
                    <MenuItem value="End of Life - Donated">
                      End of Life - Donated
                    </MenuItem>
                    <MenuItem value="End of Life - Destroyed">
                      End of Life - Destroyed
                    </MenuItem>
                    <MenuItem value="End of Life - Sold">
                      End of Life - Sold
                    </MenuItem>
                    <MenuItem value="End of Life - Returned to Vendor">
                      End of Life - Returned to Vendor
                    </MenuItem>
                    <MenuItem value="Obsolete - Recycled">
                      Obsolete - Recycled
                    </MenuItem>
                    <MenuItem value="Obsolete - Donated">
                      Obsolete - Donated
                    </MenuItem>
                    <MenuItem value="Obsolete - Destroyed">
                      Obsolete - Destroyed
                    </MenuItem>
                    <MenuItem value="Obsolete - Sold">Obsolete - Sold</MenuItem>
                    <MenuItem value="Obsolete - Returned to Vendor">
                      Obsolete - Returned to Vendor
                    </MenuItem>
                    <MenuItem value="Damaged Beyond Repair - Recycled">
                      Damaged Beyond Repair - Recycled
                    </MenuItem>
                    <MenuItem value="Damaged Beyond Repair - Destroyed">
                      Damaged Beyond Repair - Destroyed
                    </MenuItem>
                    <MenuItem value="Lost">Lost</MenuItem>
                    <MenuItem value="Stolen">Stolen</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Disposal Notes"
                  multiline
                  rows={3}
                  value={disposeData.disposalNotes}
                  onChange={(e) =>
                    setDisposeData({
                      ...disposeData,
                      disposalNotes: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Method of Data Wiping</InputLabel>
                  <Select
                    value={disposeData.dataWipeMethod}
                    label="Method of Data Wiping"
                    onChange={(e) =>
                      setDisposeData({
                        ...disposeData,
                        dataWipeMethod: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Physically destroyed">
                      Physically destroyed
                    </MenuItem>
                    <MenuItem value="External data wiping">
                      External data wiping
                    </MenuItem>
                    <MenuItem value="Internal low level data wipe">
                      Internal low level data wipe
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Certificate of Disposal"
                  multiline
                  rows={3}
                  value={disposeData.disposalCertificate}
                  onChange={(e) =>
                    setDisposeData({
                      ...disposeData,
                      disposalCertificate: e.target.value,
                    })
                  }
                  helperText="Enter certificate details or link to SharePoint file"
                  placeholder="e.g., Certificate number or SharePoint file link"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Completed By"
                  value={disposeData.disposalCompletedBy}
                  onChange={(e) =>
                    setDisposeData({
                      ...disposeData,
                      disposalCompletedBy: e.target.value,
                    })
                  }
                  placeholder="Name of person who completed disposal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Date Completed"
                  value={disposeData.disposalCompletedDate}
                  onChange={(date) =>
                    setDisposeData({
                      ...disposeData,
                      disposalCompletedDate: date,
                    })
                  }
                  textField={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDisposeDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDispose}
              variant="contained"
              color="error"
              disabled={saving}
            >
              {saving ? "Disposing..." : "Dispose Asset"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Assign Dialog */}
        <Dialog
          open={bulkAssignDialogOpen}
          onClose={() => setBulkAssignDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Bulk Assign Owners</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Assigning owners to {selectedRows.length} selected asset(s).
            </Alert>
            {formError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setFormError("")}
              >
                {formError}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Primary Owner
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find((u) => u.id === bulkAssignData.ownerId) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setBulkAssignData({
                      ...bulkAssignData,
                      ownerId: newValue?.id || "",
                      ownerEmail: newValue?.email || "",
                      ownerName: newValue?.name || "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Primary Owner"
                      placeholder="Search for owner..."
                      fullWidth
                    />
                  )}
                  loading={loadingUsers}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Secondary Owner (Optional)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find(
                      (u) => u.id === bulkAssignData.secondaryOwnerId,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setBulkAssignData({
                      ...bulkAssignData,
                      secondaryOwnerId: newValue?.id || "",
                      secondaryOwnerEmail: newValue?.email || "",
                      secondaryOwnerName: newValue?.name || "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Secondary Owner"
                      placeholder="Search for secondary owner..."
                      fullWidth
                    />
                  )}
                  loading={loadingUsers}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssign}
              variant="contained"
              disabled={
                saving ||
                (!bulkAssignData.ownerId && !bulkAssignData.secondaryOwnerId)
              }
            >
              {saving ? "Assigning..." : "Assign Owners"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default AssetRegister;
